import type { CompetitorRecord } from "@/lib/compare/types";

const HOME = "https://vsub.io";
const PRICING = "https://vsub.io/pricing";
const API_DOCS = "https://vsub.io/docs/api";
const API_V3 = "https://vsub.io/docs/api/ai-videos-v3";
const TERMS = "https://vsub.io/terms-of-service";
const CHECKED = "2026-10-08";

export const VSUB: CompetitorRecord = {
  slug: "vsub",
  name: "Vsub",
  aliases: ["VSub", "vsub.io", "Vsub AI"],
  url: HOME,
  pricingUrl: PRICING,
  category: "faceless",
  positioning: "All-in-one solution for your faceless channel: illustrated AI videos drawn scene by scene in a chosen or cloned art style.",
  plans: [
    {
      name: "Premium",
      monthly: 99,
      metered: "27,500 credits per month, about 81 minutes of rendered video",
      includes: [
        "Video render at 10 credits per minute",
        "Subtitles at 20 credits per video",
        "TTS from 42 to 140 credits per 1,000 characters",
        "AI images at 250 credits per 20",
        "Top-up credits at $1 for 275",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "No annual or lifetime price is shown; the Lifetime toggle renders no figures.",
    },
    {
      name: "Premium+",
      monthly: 199,
      metered: "66,000 credits per month, about 193 minutes of rendered video",
      includes: [
        "Marked Most popular on the pricing page",
        "Same per-action credit costs as Premium",
        "Top-up credits at $1 for 330",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Monthly price only; no annual figure published.",
    },
    {
      name: "Max",
      monthly: 499,
      metered: "206,000 credits per month, about 616 minutes of rendered video",
      includes: [
        "Same per-action credit costs as Premium",
        "Top-up credits at $1 for 412",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Monthly price only; no annual figure published.",
    },
  ],
  entryPlanName: "Premium",
  entryPlanIncludes: "27,500 credits a month, about 81 minutes of rendered video before subtitles, narration and images are counted",
  freeTier: "None. The API docs say rendering requires a paid plan unless you bring your own Google AI Studio key.",
  trial: "None listed on the pricing page",
  meteringUnit: "Credits per action: 10 per rendered minute, 20 per subtitled video, 42 to 140 per 1,000 narrated characters, 250 per 20 images",
  exportResolution: "1080p, 2K and 4K listed with no per-plan mapping; the homepage says 16:9 at 1080p, portrait via the API",
  features: {
    longToShort: { v: "no", note: "Not offered on the site or API" },
    momentRanking: { v: "no" },
    reframing: { v: "no", note: "Generates from a script, no footage" },
    maxSource: { v: "text", note: "Plan-limited, not published; scripts to 30,000 characters" },
    layouts: { v: "n/a", note: "Art styles instead; 16:9 default, portrait via API" },
    redditStory: { v: "partial", note: "Story videos from any script, no Reddit template" },
    fakeText: { v: "yes", note: "Fake text template plus free screenshot tools" },
    aiImages: { v: "yes", note: "Per-scene illustrations, 250 credits per 20" },
    voiceover: { v: "yes", note: "ElevenLabs v2, v4, v4 Turbo or Vsub engine" },
    voiceCloning: { v: "no", note: "Not listed; upload your own voiceover instead" },
    avatars: { v: "yes", note: "720p lip-sync at 6 credits per second" },
    timelineEditor: { v: "partial", note: "Redraw single scenes; no timeline" },
    captionStyles: { v: "n/a", note: "Template library, count not published" },
    captionLanguages: { v: "n/a", note: "Not published" },
    bRoll: { v: "no", note: "AI illustrations only, no stock or gameplay" },
    fourK: { v: "yes", note: "Listed, no per-plan mapping" },
    scheduling: { v: "n/a", note: "Not mentioned on homepage, pricing or API" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "Video templates, TTS, captions, MCP server" },
    webhooks: { v: "yes", note: "Listed in the API docs" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Not advertised" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "n/a", note: "Workspace tokens exist; seat count not published" },
    freeTier: { v: "no", note: "Paid plan required unless BYOK" },
    trial: { v: "no", note: "None listed" },
    metering: { v: "text", note: "Credits per minute, character and image" },
    rollover: { v: "n/a", note: "FAQ heading renders without an answer" },
    refunds: { v: "text", note: "Not stated on pricing page or terms" },
    cancelInApp: { v: "n/a", note: "FAQ heading renders without an answer" },
  },
  billingTerms: {
    refunds:
      "Not stated. The pricing page FAQ heading \"Do you have a refund policy?\" renders without an answer as of Oct 8, 2026 (vsub.io/pricing), and the terms of service add nothing on refunds (vsub.io/terms-of-service).",
    cancel:
      "Not stated. The pricing page FAQ heading \"How do I cancel my plan?\" renders without an answer as of Oct 8, 2026 (vsub.io/pricing), and the terms of service do not describe cancellation.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: `Pricing page fetched directly on Oct 8, 2026: Premium $99, Premium+ $199 and Max $499 per month with 27,500, 66,000 and 206,000 credits, the per-action credit costs and the top-up rates all read from the fetched page. No annual or lifetime figures rendered, so none are recorded. Generation times (up to 3 hours standard, 6 hours slow lane, 24 hours BYOK batch) and the $0.10 per minute BYOK rate come from ${API_V3}; the API surface from ${API_DOCS}; refund and cancel absence from ${PRICING} and ${TERMS}. Vsub's own October 2024 reply on Trustpilot referenced a $49 Pro plan and third-party directories still quote $29, $49 and $99 tiers; those are not on the live page and are not used here. Nothing needs a browser check unless the lead wants the empty FAQ answers and the Lifetime toggle confirmed in a real browser.`,
  },
  seoPattern: "No vs, compare or alternatives pages and no blog; SEO is free fake-text screenshot tools at vsub.io/tools",
};
